import { Fragment, useCallback, useState } from 'react';
import { FileSearch, ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react';
import { auditReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, CARD_CLS,
  today, firstOfMonth, fmtDate, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import { Pill, CountChip, Pager, Dash, errMessage, DEFAULT_PAGE_SIZE } from './ReportCentreKit';
import type { PillTone } from './ReportCentreKit';
import type { AuditTrailFilter, AuditTrailReportDto, AuditTrailRow } from '@/types/reportCentre.types';

const REPORT_KEY = 'AUDIT_TRAIL';

const ACTION_TONES: Record<string, PillTone> = {
  CREATE:  'green',
  UPDATE:  'blue',
  DELETE:  'red',
  POST:    'purple',
  APPROVE: 'green',
  REJECT:  'red',
  EXPORT:  'amber',
  PRINT:   'amber',
};

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** Pretty-print a JSON string; fall back to the raw text when it is not valid JSON. */
function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

// ── Export ────────────────────────────────────────────────────────────────────

function buildColumns(sensitive: boolean): ExcelColumn[] {
  const cols: ExcelColumn[] = [
    { key: 'Entity',      header: 'Entity',         width: 24 },
    { key: 'EntityId',    header: 'Entity Id',      width: 38 },
    { key: 'Action',      header: 'Action',         width: 12 },
    { key: 'By',          header: 'By',             width: 22 },
    { key: 'At',          header: 'At',             width: 20 },
    { key: 'Remarks',     header: 'Remarks',        width: 40 },
    { key: 'Correlation', header: 'Correlation Id', width: 34 },
  ];
  if (sensitive) {
    cols.push(
      { key: 'Ip',            header: 'IP Address',     width: 16 },
      { key: 'ChangedFields', header: 'Changed Fields', width: 30 },
      { key: 'OldValue',      header: 'Old Value',      width: 60 },
      { key: 'NewValue',      header: 'New Value',      width: 60 },
    );
  }
  return cols;
}

function buildExportRows(rows: AuditTrailRow[], sensitive: boolean): Record<string, unknown>[] {
  return rows.map(r => {
    const base: Record<string, unknown> = {
      Entity:      r.entityType,
      EntityId:    r.entityId ?? '',
      Action:      r.action,
      By:          r.changedByName ?? '',
      At:          r.actionDate,
      Remarks:     r.remarks ?? '',
      Correlation: r.correlationId ?? '',
    };
    if (sensitive) {
      base.Ip            = r.ipAddress ?? '';
      base.ChangedFields = (r.changedFields ?? []).join(', ');
      base.OldValue      = r.oldValueJson ?? '';
      base.NewValue      = r.newValueJson ?? '';
    }
    return base;
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

export const AuditTrailPage = () => {
  const { hasPermission } = usePermission();
  const canSeeSensitive = hasPermission(Permissions.RPT_SENSITIVE);

  const [filter, setFilter] = useState<AuditTrailFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [sensitive, setSensitive] = useState(false);
  const [applied, setApplied] = useState<{ filter: AuditTrailFilter; sensitive: boolean } | null>(null);
  const [data, setData] = useState<AuditTrailReportDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const fetchPage = useCallback(async (base: AuditTrailFilter, useSensitive: boolean, pageNumber: number) => {
    const query = { ...base, pageNumber };
    setLoading(true);
    setError(null);
    try {
      const res = useSensitive
        ? await auditReportApi.getAuditTrailSensitive(query)
        : await auditReportApi.getAuditTrail(query);
      setData(res.data?.data ?? null);
      setApplied({ filter: query, sensitive: useSensitive });
      setExpanded(new Set());
    } catch (e: unknown) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const run = useCallback(() => {
    void fetchPage(filter, canSeeSensitive && sensitive, 1);
  }, [fetchPage, filter, sensitive, canSeeSensitive]);

  if (!hasPermission(Permissions.RPT_AUDIT_TRAIL)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const page = data?.rows;
  const items = page?.items ?? [];
  // Trust the server flag, not the local toggle — sensitive columns render only when data has them.
  const showSensitive = data?.includesSensitiveData ?? false;
  const colCount = showSensitive ? 9 : 7;

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExcel = () => {
    if (!data || !page) return;
    exportToExcel({
      sheetName: 'Audit Trail',
      columns: buildColumns(showSensitive),
      rows: buildExportRows(items, showSensitive),
      title: `Audit Trail${showSensitive ? ' (sensitive)' : ''}`,
      subtitle: `${data.fromDate} to ${data.toDate} · Page ${page.pageNumber} of ${page.totalPages}`,
    }, `AuditTrail_${data.fromDate}_${data.toDate}_p${page.pageNumber}`);
  };

  const handleCsv = () => {
    if (!data || !page) return;
    exportToCsvRows(
      buildColumns(showSensitive),
      buildExportRows(items, showSensitive),
      `AuditTrail_${data.fromDate}_${data.toDate}_p${page.pageNumber}`,
    );
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileSearch size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Audit Trail</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={!!data}
          reportKey={REPORT_KEY}
          rowCount={items.length}
          filterSummary={`${filter.fromDate}..${filter.toDate}${showSensitive ? ' sensitive' : ''}`}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4 space-y-3')}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className={LABEL_CLS}>From Date *</label>
            <input type="date" value={filter.fromDate} onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))} className={INPUT_CLS} />
          </div>
          <div>
            <label className={LABEL_CLS}>To Date *</label>
            <input type="date" value={filter.toDate} onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))} className={INPUT_CLS} />
          </div>
          <div>
            <label className={LABEL_CLS}>Entity Type</label>
            <input
              type="text"
              value={filter.entityType ?? ''}
              placeholder="e.g. GlJournal"
              onChange={e => setFilter(f => ({ ...f, entityType: e.target.value || undefined }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Action</label>
            <input
              type="text"
              value={filter.action ?? ''}
              placeholder="e.g. UPDATE"
              onChange={e => setFilter(f => ({ ...f, action: e.target.value || undefined }))}
              className={INPUT_CLS}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* The sensitive toggle only exists for users holding VIEW_RPT_SENSITIVE. */}
          {canSeeSensitive ? (
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={sensitive}
                onChange={e => setSensitive(e.target.checked)}
                className="rounded"
              />
              <ShieldCheck size={14} className="text-amber-500" />
              Show sensitive data (IP, before/after values)
            </label>
          ) : <span />}
          <RunReportButton onClick={run} isLoading={loading} disabled={!filter.fromDate || !filter.toDate} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
      )}

      {!data && !loading && (
        <EmptyState message='Set the date range and click "Run Report" to browse the audit trail.' />
      )}

      {data && page && (
        <div className="flex flex-col gap-4">
          {/* Action count chips — whole window */}
          <div className="flex flex-wrap items-center gap-2">
            {data.actionCounts.map(c => (
              <CountChip key={c.action} label={c.action} count={c.count} tone={ACTION_TONES[c.action] ?? 'grey'} />
            ))}
          </div>

          {/* Table */}
          <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                Audit Entries
                {showSensitive && (
                  <span className="ml-2 align-middle"><Pill label="SENSITIVE VIEW" tone="amber" /></span>
                )}
              </h2>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
              </span>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-sm border-collapse min-w-[1200px]">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                  <tr>
                    {showSensitive && <th className={cn(TH_CLS, 'w-8')}></th>}
                    <th className={TH_CLS}>Entity</th>
                    <th className={cn(TH_CLS, 'w-28')}>Action</th>
                    <th className={TH_CLS}>By</th>
                    <th className={cn(TH_CLS, 'w-40')}>At</th>
                    <th className={TH_CLS}>Remarks</th>
                    <th className={cn(TH_CLS, 'w-36')}>Correlation</th>
                    {showSensitive && <th className={cn(TH_CLS, 'w-32')}>IP</th>}
                    {showSensitive && <th className={TH_CLS}>Changed Fields</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={colCount} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                        No audit entries match the selected criteria.
                      </td>
                    </tr>
                  ) : items.map(r => {
                    const isOpen = expanded.has(r.id);
                    const expandable = showSensitive && (r.oldValueJson !== null || r.newValueJson !== null);
                    return (
                      <Fragment key={r.id}>
                        <tr
                          onClick={expandable ? () => toggleExpand(r.id) : undefined}
                          className={cn(
                            'border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50',
                            expandable && 'cursor-pointer',
                          )}
                        >
                          {showSensitive && (
                            <td className="px-3 py-2 text-gray-400">
                              {expandable ? (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : null}
                            </td>
                          )}
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {r.entityType}
                            {r.entityId && (
                              <span className="block font-mono text-[10px] text-gray-400 dark:text-gray-500 truncate max-w-[220px]" title={r.entityId}>
                                {r.entityId}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <Pill label={r.action} tone={ACTION_TONES[r.action] ?? 'grey'} />
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.changedByName ?? <Dash />}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDateTime(r.actionDate)}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.remarks ?? <Dash />}</td>
                          <td className="px-3 py-2">
                            {r.correlationId
                              ? <span className="font-mono text-[10px] text-gray-500 dark:text-gray-400 truncate inline-block max-w-[130px]" title={r.correlationId}>{r.correlationId}</span>
                              : <Dash />}
                          </td>
                          {showSensitive && (
                            <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400">{r.ipAddress ?? <Dash />}</td>
                          )}
                          {showSensitive && (
                            <td className="px-3 py-2">
                              {r.changedFields && r.changedFields.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {r.changedFields.map(f => (
                                    <span key={f} className="inline-block px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-[10px] font-mono">
                                      {f}
                                    </span>
                                  ))}
                                </div>
                              ) : <Dash />}
                            </td>
                          )}
                        </tr>
                        {isOpen && expandable && (
                          <tr className="border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30">
                            <td colSpan={colCount} className="px-6 py-3">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                <div>
                                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Old Value</div>
                                  {r.oldValueJson ? (
                                    <pre className="text-[11px] font-mono bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 overflow-auto max-h-72 whitespace-pre-wrap break-all">
                                      {prettyJson(r.oldValueJson)}
                                    </pre>
                                  ) : <Dash />}
                                </div>
                                <div>
                                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">New Value</div>
                                  {r.newValueJson ? (
                                    <pre className="text-[11px] font-mono bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 overflow-auto max-h-72 whitespace-pre-wrap break-all">
                                      {prettyJson(r.newValueJson)}
                                    </pre>
                                  ) : <Dash />}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pager
              pageNumber={page.pageNumber}
              totalPages={page.totalPages}
              totalCount={page.totalCount}
              shownCount={items.length}
              loading={loading}
              onPage={p => {
                if (applied) void fetchPage(applied.filter, applied.sensitive, p);
              }}
              noun="entries"
            />
          </div>
        </div>
      )}
    </div>
  );
};
